import { OrgActions } from "common/reducers/org.actions";
import { duplicate, list, load, loadCustomerOrg, sync, test, update } from "common/api/org.service";
import { handleErrors } from "common/reducers/Utils";
import { DANGER, addFlashAlert } from "common/components/FlashAlert";
import { currentOrg, currentOrgId, currentOrgSettingsConfig } from "common/selectors/org.selectors";
import { RootState, ThunkResult } from "common/reducers";
import { RootDispatchType, RootGetState } from "common/store";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { TaskActions } from "common/reducers/task.actions";
import { TokenActions } from "common/reducers/token.actions";
import { List } from "immutable";
import { form } from "common/selectors/rsfForm.selectors";
import { TokenKind } from "common/records/Token";
import { token } from "common/selectors/token.selectors";
import { cleanProps } from "common/api/token.service";
import { Nullable } from "common/types";
import { OrgParamsRecord } from "common/records/index/OrgParams";
import { IGlobalRequestOptions } from "common/api/Fetcher";
import { commissionTestData } from "common/selectors/estimate.selectors";
import { EstimateAsyncActions } from "common/reducers/estimate.async.actions";
import { required } from "common/helpers/FormValidate";
import { JobResultingActions } from "common/reducers/jobResulting.actions";
import { jobResultingConfig } from "common/selectors/jobResulting.selectors";

export const OrgAsyncActions = {
  listOrgs: (queryParams: OrgParamsRecord, options?: IGlobalRequestOptions): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(IdsManagementActions.fetchModel("orgOverviews", queryParams));
        const { orgs, meta } = await (options ? list(queryParams, options) : list(queryParams));
        const ids = List(orgs.map((org) => org.id));
        dispatch(OrgActions.receiveOrgOverviews(orgs));
        dispatch(IdsManagementActions.receiveModel("orgOverviews", ids, meta?.pagination));
      } catch (errors) {
        handleErrors(errors);
        dispatch(IdsManagementActions.receiveModel("orgOverviews", List()));
      }
    };
  },
  getOrg: (id: number, set_current_org = false): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(OrgActions.fetchOrg(id));
        if (set_current_org) {
          dispatch(OrgActions.setCurrentOrgId(id));
        }

        const { org } = await load(id);

        dispatch(OrgActions.receiveOrg(org));
      } catch (response) {
        dispatch(OrgActions.setOrgLoaded(id));

        const errors = handleErrors(response);
        dispatch(OrgActions.setOrgErrors(id, errors));
      }
    };
  },
  updateOrg: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgRecord = currentOrg(getState());
      if (!orgRecord) {
        addFlashAlert("danger", "No organization selected");
        return;
      }

      try {
        dispatch(OrgActions.fetchOrg(orgRecord.id));

        const { org } = await update(orgRecord);

        dispatch(OrgActions.receiveOrg(org));
        addFlashAlert("success", "Saved organization successfully");
      } catch (errors) {
        dispatch(OrgActions.setOrgLoaded(orgRecord.id));
        handleErrors(errors);
      }
    };
  },
  duplicateOrg: () => {
    return async (dispatch: RootDispatchType, getState: () => RootState): Promise<void> => {
      const sourceOrgId = currentOrgId(getState());
      const duplicateOrgForm = form(getState(), { name: "duplicateOrg" });

      if (!sourceOrgId) {
        addFlashAlert("danger", "No source org selected");
        return;
      }
      if (!duplicateOrgForm) {
        addFlashAlert("danger", "No items selected to duplicate");
        return;
      }

      const selectedItems = duplicateOrgForm.get("items") as string[];
      const selectedOrgs = duplicateOrgForm.get("selectedOrgs") as List<number>;

      return new Promise((resolve, reject) => {
        selectedOrgs.forEach(async (orgId, index) => {
          try {
            const { task } = await duplicate(orgId, sourceOrgId, selectedItems);

            dispatch(TaskActions.receiveTask(task));

            addFlashAlert("success", `Submitted task to duplicate org ${orgId} successfully.`);

            if (index === selectedOrgs.size - 1) resolve();
          } catch (errors) {
            handleErrors(errors);
            if (index === selectedOrgs.size - 1) resolve();
          }
        });
      });
    };
  },
  syncIntegration: (integration: TokenKind) => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const orgId = currentOrgId(getState());

      if (!orgId) {
        addFlashAlert("danger", "No organization selected");
        return;
      }

      try {
        await sync(orgId, {
          process: "InitialSyncJob",
          integration,
        });
        addFlashAlert("success", "Sync Requested Successfully");
      } catch (response) {
        addFlashAlert(
          DANGER,
          "There were problems requesting the sync. Make sure all your information is correct. If the problem continues, contact support.",
        );
      }
    };
  },
  testIntegration: (tokenKind: Nullable<TokenKind>): ThunkResult<Promise<{ taskId: string } | undefined>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      if (!tokenKind) {
        addFlashAlert("danger", "No token kind selected");
        return;
      }
      const orgId = currentOrgId(getState());
      if (!orgId) {
        addFlashAlert("danger", "No organization selected");
        return;
      }

      try {
        dispatch(TokenActions.fetchToken(orgId, tokenKind));
        const tokenRecord = token(getState(), { kind: tokenKind });
        if (!tokenRecord) {
          addFlashAlert("danger", "No token selected");
          return;
        }
        const response = await test(orgId, { kind: tokenKind, token: cleanProps(tokenRecord) });
        dispatch(TaskActions.receiveTask(response.task));
        if (response.task.status !== "finished") {
          console.error(response.task);
          addFlashAlert(
            "danger",
            "There was a problem with your settings, review the log and try again. If the problem continues, contact support.",
          );
        }
        dispatch(TokenActions.setLoaded(orgId, tokenKind));
        return { taskId: response.task.id };
      } catch (response) {
        dispatch(TokenActions.setLoaded(orgId, tokenKind));
        handleErrors(response);
      }
    };
  },

  getCustomerOrg: (uuid: string): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(IdsManagementActions.fetchModel("customerOrg"));
      try {
        const { org } = await loadCustomerOrg(uuid);
        dispatch(OrgActions.setCurrentOrgId(org.id));
        dispatch(OrgActions.receiveOrg(org));
      } catch (errors) {
        handleErrors(errors, false);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("customerOrg"));
      }
    };
  },
  saveEstimatorSettings: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const state = getState();
      const commission_method = currentOrgSettingsConfig(state, {
        path: ["estimator", "commission_calculator", "method"],
      });
      const testData = commissionTestData(state);

      if (commission_method === "custom") {
        const estimateId = testData.get("estimate_id");
        if (!estimateId) {
          addFlashAlert(
            "danger",
            "Please select a job and an estimate to validate commission configuration before saving",
          );
          return;
        }

        const isValid = await dispatch(EstimateAsyncActions.validateCommission(estimateId));
        if (!isValid) {
          addFlashAlert("danger", "Commission configuration validation failed.");
          return;
        }
      }

      dispatch(OrgAsyncActions.updateOrg());
    };
  },
  saveGlobalSettings: (): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const config = jobResultingConfig(getState());

      let hasErrors = false;

      // Only validate Job Resulting configuration if it's enabled
      if (config.get("enabled")) {
        config.get("options")?.forEach((option: any) => {
          const propsSize = option.get("schema")?.get("properties")?.size || 0;
          if (propsSize < 1) {
            addFlashAlert("danger", `Job Resulting Option "${option.get("name")}" must have at least one field.`);
            hasErrors = true;
            return;
          }
          const requiredError = required(option.get("to_email"));
          if (requiredError) {
            addFlashAlert(
              "danger",
              `Job Resulting Option "${option.get("name")}" must have a "To Email Recipients" value.`,
            );
            hasErrors = true;
            return;
          }
        });
      }

      if (hasErrors) return;

      const orgId = currentOrgId(getState());
      if (!orgId) {
        addFlashAlert("danger", "No organization selected");
        return;
      }

      dispatch(JobResultingActions.updateOrgJobResultingConfig(orgId));
      await dispatch(OrgAsyncActions.updateOrg());
    };
  },
};
